import { IWorklistSidebarStore } from '../WorklistSidebarStore';
import { IWorklistStore } from '../../../Stores/WorklistStore';
import { isEntityLoading } from '@/Models/Entity';
import { ICustomWorklistHelper } from '@/Pages/GenericWorklist/Stores/CustomWorklistHelper';
import { ICurrentWorklistDashboardHelper } from '@/Pages/GenericWorklist/Stores/CurrentWorklistDashboardHelper';

export interface IWorklistSidebarFooterViewHelper {
    addCustomWorklistButtonTooltipTextKey: string;
    deleteCustomWorklistButtonTooltipTextKey: string;
    editCustomWorklistButtonTooltipTextKey: string;
    isAddCustomWorklistButtonDisabled: boolean;
    isApplyDisabled: boolean;
    isClearDisabled: boolean;
    isEditCustomWorklistButtonAvailable: boolean;
    isEditCustomWorklistButtonDisabled: boolean;
    isRestoreDisabled: boolean;
    worklistDashboardButtonTooltipText: string;
    restoreActiveCustomWorklist: () => void;
    clearCurrentFilter: () => void;
}

export class WorklistSidebarFooterViewHelper implements IWorklistSidebarFooterViewHelper {
    private readonly currentWorklistDashboardHelper: ICurrentWorklistDashboardHelper;
    private readonly customWorklistHelper: ICustomWorklistHelper;
    private readonly worklistSidebarStore: IWorklistSidebarStore;
    private readonly worklistStore: IWorklistStore;

    constructor(
        currentWorklistDashboardHelper: ICurrentWorklistDashboardHelper,
        customWorklistHelper: ICustomWorklistHelper,
        worklistSidebarStore: IWorklistSidebarStore,
        worklistStore: IWorklistStore,
    ) {
        this.currentWorklistDashboardHelper = currentWorklistDashboardHelper;
        this.customWorklistHelper = customWorklistHelper;
        this.worklistSidebarStore = worklistSidebarStore;
        this.worklistStore = worklistStore;
    }

    get addCustomWorklistButtonTooltipTextKey(): string {
        const currentFilterIsIdenticalToActiveFilter = this.worklistSidebarStore.isCurrentFilterIdenticalToActiveFilter;
        const resultsAreLoading = this._areWorklistResultsLoading;

        if (resultsAreLoading) {
            return 'Loading';
        }
        else if (currentFilterIsIdenticalToActiveFilter) {
            return 'SaveAsNewWorklist';
        }
        else {
            return 'Worklist.ActiveAndCurrentFilterMismatch';
        }
    }

    get deleteCustomWorklistButtonTooltipTextKey(): string {
        const resultsAreLoading = this._areWorklistResultsLoading;
        const userIsAllowedToEdit = this.customWorklistHelper.userCanEditCurrentCustomWorklist;
        const currentFilterIsIdenticalToActiveFilter = this.worklistSidebarStore.isCurrentFilterIdenticalToActiveFilter;
        const activeFilterMatchesActiveCustomWorklist = this.customWorklistHelper.isActiveWorklistIdenticalToActiveCustomWorklist;

        if (resultsAreLoading) {
            return 'Loading';
        }
        else if (!userIsAllowedToEdit) {
            return 'NotAllowed';
        }
        else if (!currentFilterIsIdenticalToActiveFilter) {
            return 'Worklist.ActiveAndCurrentFilterMismatch';
        }
        else if (activeFilterMatchesActiveCustomWorklist) {
            return 'Worklist.DeleteWorklist';
        }
        else {
            return 'Worklist.ActiveFilterAndActiveCustomWorklistMismatch';
        }
    }

    get editCustomWorklistButtonTooltipTextKey(): string {
        const resultsAreLoading = this._areWorklistResultsLoading;
        const userIsAllowedToEdit = this.customWorklistHelper.userCanEditCurrentCustomWorklist;
        const currentFilterIsIdenticalToActiveFilter = this.worklistSidebarStore.isCurrentFilterIdenticalToActiveFilter;

        if (resultsAreLoading) {
            return 'Loading';
        }
        else if (!userIsAllowedToEdit) {
            return 'NotAllowed';
        }
        else if (currentFilterIsIdenticalToActiveFilter) {
            return 'SaveWorklist';
        }
        else {
            return 'Worklist.ActiveAndCurrentFilterMismatch';
        }
    }

    get isApplyDisabled(): boolean {
        return this._areWorklistResultsLoading;
    }

    get isRestoreDisabled(): boolean {
        return this.worklistSidebarStore.isRestoreDisabled;
    }

    get isClearDisabled(): boolean {
        return this._areWorklistResultsLoading;
    }

    get isAddCustomWorklistButtonDisabled(): boolean {
        const currentFilterIsIdenticalToActiveFilter = this.worklistSidebarStore.isCurrentFilterIdenticalToActiveFilter;
        const resultsAreLoading = this._areWorklistResultsLoading;

        return resultsAreLoading || !currentFilterIsIdenticalToActiveFilter;
    }

    get isDeleteCustomWorklistButtonDisabled(): boolean {
        if (!this.customWorklistHelper.isDeleteCustomWorklistButtonAvailable) {
            return false;
        }
        return !this._validToDeleteWorklist;
    }

    get isEditCustomWorklistButtonAvailable(): boolean {
        return !!this.customWorklistHelper.currentlySelectedCustomWorklist
            && this.customWorklistHelper.userCanEditCurrentCustomWorklist;
    }

    get isEditCustomWorklistButtonDisabled(): boolean {
        if (!this.isEditCustomWorklistButtonAvailable) {
            return false;
        }
        return !this._validToEditWorklist;
    }

    get isWorklistDashboardButtonDisabled(): boolean {
        if (!this.customWorklistHelper.currentlySelectedCustomWorklist) {
            return false;
        }

        const notInDashboardAndNoSpaceLeft = this.currentWorklistDashboardHelper.isNoSpaceLeftForCurrentNewDashboard;

        const allowed = !this._areWorklistResultsLoading
            && this.customWorklistHelper.currentlySelectedCustomWorklist
            && this._currentAndActiveFilterMatchCustomWorklist
            && !notInDashboardAndNoSpaceLeft;

        return !allowed;
    }

    get isWorklistDashboardButtonAvailable(): boolean {
        return !!this.customWorklistHelper.currentlySelectedCustomWorklist;
    }

    get worklistDashboardButtonTooltipText(): string {
        const resultsAreLoading = this._areWorklistResultsLoading;
        const customSelected = this.customWorklistHelper.currentlySelectedCustomWorklist;
        const isCurrentFilterIdenticalToActiveFilter = this.worklistSidebarStore.isCurrentFilterIdenticalToActiveFilter;
        const isActiveFilterIdenticalToActiveCustomWorklist = this.customWorklistHelper.isActiveWorklistIdenticalToActiveCustomWorklist;
        const notInDashboardAndNoSpaceLeft = this.currentWorklistDashboardHelper.isNoSpaceLeftForCurrentNewDashboard;

        if (resultsAreLoading) {
            return 'Loading';
        }
        else if (!customSelected) {
            return 'Worklist.NoCustomWorklistSelected';
        }
        else if (!isCurrentFilterIdenticalToActiveFilter) {
            return 'Worklist.ActiveAndCurrentFilterMismatch';
        }
        else if (!isActiveFilterIdenticalToActiveCustomWorklist) {
            return 'Worklist.ActiveFilterAndActiveCustomWorklistMismatch';
        }
        else if (notInDashboardAndNoSpaceLeft) {
            return 'Worklist.NoSpotsLeftInDashboard';
        }
        else {
            return this.currentWorklistDashboardHelper.dialogActionTitleKey;
        }
    }

    restoreActiveCustomWorklist = (): void => {
        const currentCustomWorklist = this.customWorklistHelper.currentlySelectedCustomWorklist;
        if (!currentCustomWorklist) {
            console.warn('⚠️ Should not occur, attempted to restore a custom worklist while none is selected.');
            return;
        }

        const customWorklistFilterParams = currentCustomWorklist.Worklist.Params.Filter;
        this.worklistSidebarStore.setCurrentFilter(customWorklistFilterParams);
        this.worklistSidebarStore.applyCurrentFilter();
    };

    clearCurrentFilter = (): void => {
        this.worklistSidebarStore.setCurrentFilter(undefined);
    };

    get _areWorklistResultsLoading(): boolean {
        return isEntityLoading(this.worklistStore.results);
    }

    get _validToDeleteWorklist(): boolean {
        const allowedToEditWorklist = this._validToEditWorklist;
        const currentAndActiveMatchCustom = this._currentAndActiveFilterMatchCustomWorklist;

        return allowedToEditWorklist && currentAndActiveMatchCustom;
    }

    get _validToEditWorklist(): boolean {
        const resultsAreLoading = this._areWorklistResultsLoading;
        const userIsAllowedToEdit = this.customWorklistHelper.userCanEditCurrentCustomWorklist;
        const currentFilterIsIdenticalToActiveFilter = this.worklistSidebarStore.isCurrentFilterIdenticalToActiveFilter;

        return !resultsAreLoading && userIsAllowedToEdit && currentFilterIsIdenticalToActiveFilter;
    }

    get _currentAndActiveFilterMatchCustomWorklist(): boolean {
        const isCurrentFilterIdenticalToActiveFilter = this.worklistSidebarStore.isCurrentFilterIdenticalToActiveFilter;
        const isActiveFilterIdenticalActiveCustomWorklist = this.customWorklistHelper.isActiveWorklistIdenticalToActiveCustomWorklist;

        return isCurrentFilterIdenticalToActiveFilter && isActiveFilterIdenticalActiveCustomWorklist;
    }
}
